<div class="flex flex-col items-center space-y-4 mb-4">
    @if (!resultsExpired) {
        <h1 class="text-center !text-3xl !font-normal text-tertiary-900 !m-0">
            Results ready!
        </h1>

        <h2 class="text-center !text-base !font-medium text-tertiary-900">
            Cevo has selected the frequencies below to best match your
            application requirements. They are reserved for a limited time only,
            as specified.
        </h2>
    } @else {
        <h1 class="text-center !text-3xl !font-normal text-tertiary-900 !m-0">
            Results have expired!
        </h1>

        <h2 class="text-center !text-base !font-medium text-tertiary-900">
            Due to sequential processing obligations, the certified frequencies
            previously reserved are no longer available. You may re-run your
            request or simply cancel. If re-run, the countdown timer will
            restart once Cevo Go reserves new frequencies.
        </h2>
    }

    <div>
        <button
            class="text-primary-800 font-medium text-sm"
            (click)="onChangeInputParameters()"
        >
            Change input parameters
        </button>

        or

        <button
            class="text-primary-800 font-medium text-sm"
            (click)="cancelSearch()"
        >
            Cancel request
        </button>
    </div>
</div>

<form [formGroup]="form" class="flex flex-col space-y-4">
    @if (goFrequencyResultsResponse !== null) {
        <app-go-application-purpose-selection
            [applicationPurpose]="
                goFrequencyResultsResponse.originalRequest.applicationPurpose
            "
            [fccRegistrationNumber]="
                goFrequencyResultsResponse.originalRequest.fccRegistrationNumber
            "
            [callSign]="goFrequencyResultsResponse.originalRequest.callSign"
            [applicationId]="applicationState?.salmonLogId!"
        ></app-go-application-purpose-selection>

        <app-go-search-information></app-go-search-information>

        <app-go-parameters-summary
            [form]="technicalDataForm"
            [expanded]="false"
        ></app-go-parameters-summary>

        <mat-expansion-panel #frequencyResultsExpansionPanel [expanded]="true">
            <mat-expansion-panel-header>
                <mat-panel-title
                    class="flex space-x-2 !text-tertiary-900 font-normal text-xl py-2"
                >
                    <h1 class="text-tertiary-900 font-normal !text-xl !m-0">
                        Results
                    </h1>

                    @if (isFrequencyResultsSectionInvalid()) {
                        <img src="assets/svg/error_icon.svg" alt="Error Icon" />

                        <p class="!mb-0 !ml-2 text-error-600 font-medium">
                            {{
                                frequencyResultMessages.atLeastOneFrequencyResultMustBeSelected
                            }}
                        </p>
                    }
                </mat-panel-title>

                <mat-panel-description>
                    @if (!frequencyResultsExpansionPanel.expanded) {
                        {{ frequencyResultsSectionDescription() }}
                    }
                </mat-panel-description>
            </mat-expansion-panel-header>

            <ag-grid-angular
                class="ag-theme-quartz"
                [context]="initializeGridContext()"
                [rowData]="rowData"
                [columnDefs]="columnDefs"
                [gridOptions]="gridOptions"
                (gridReady)="onGridReady($event)"
            ></ag-grid-angular>

            <div class="flex flex-col p-2">
                <p class="text-sm text-tertiary-600">
                    If other frequency options preferred, please provide
                    pertinent system criteria, including spacing requirements,
                    in the Comments section below, or contact EWA.
                </p>
                <h3 class="text-xl text-tertiary-900 text-right !mb-0">
                    Total Fees: ${{ totalFees }}
                </h3>
            </div>
        </mat-expansion-panel>

        <mat-expansion-panel [expanded]="true">
            <mat-expansion-panel-header>
                <mat-panel-title
                    class="flex space-x-2 !text-tertiary-900 font-normal text-xl py-2"
                >
                    <h1 class="text-tertiary-900 font-normal !text-xl !m-0">
                        Comments for EWA Advisor
                    </h1>
                </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="flex justify-evenly items-center w-full mt-2">
                <mat-form-field appearance="outline" class="w-full">
                    <mat-label>Application Comments</mat-label>
                    <textarea
                        [formControl]="commentsControl"
                        title="Application Comments"
                        matInput
                        maxlength="1000"
                        [appRegexInput]="
                            regExPattern.PrintableAsciiExceptAngleBracketsAndPipes
                        "
                        class="!h-48 !resize-none"
                    ></textarea>
                </mat-form-field>
            </div>
        </mat-expansion-panel>

        <mat-expansion-panel [expanded]="true">
            <mat-expansion-panel-header>
                <mat-panel-title
                    class="!text-tertiary-900 font-normal !text-xl py-2"
                >
                    <h1 class="text-tertiary-900 font-normal !text-xl !m-0">
                        Select Payment Method
                    </h1>

                    @if (isPaymentMethodSectionInvalid()) {
                        <img src="assets/svg/error_icon.svg" alt="Error Icon" />
                    }
                </mat-panel-title>
            </mat-expansion-panel-header>

            <div class="flex flex-col space-y-4">
                <app-go-segmented-toggle
                    formControlName="paymentMethod"
                    [options]="paymentMethodSegmentedToggleOptions"
                ></app-go-segmented-toggle>

                @if (!isApprovedForInvoiceNet30()) {
                    <span
                        class="inline-flex items-center gap-1.5 ml-auto pl-8 shrink-0"
                    >
                        <a
                            [href]="
                                externalLinks.enterpriseWireless
                                    .creditApplication
                            "
                            target="_blank"
                            rel="noopener noreferrer"
                            class="text-sm font-bold text-primary-600 hover:text-primary-700 hover:underline"
                        >
                            EWA Credit Application
                        </a>
                        <app-hover-tooltip
                            [moreInfo]="true"
                            [message]="
                                orderPaymentConstants.INVOICE_NET_30_CREDIT_APPLICATION_TOOLTIP
                            "
                        ></app-hover-tooltip>
                    </span>
                }

                @if (isAuthNetAim(paymentMethodControl.value)) {
                    <div class="flex items-start w-full">
                        <mat-form-field
                            appearance="outline"
                            class="flex-1 w-full"
                            subscriptSizing="dynamic"
                        >
                            <mat-label>Credit Card</mat-label>
                            <mat-select
                                [formControl]="cardOnFileIdControl"
                                panelWidth="w-full"
                                placeholder="Please select or add a credit card"
                            >
                                @for (
                                    cardOption of cardsOnFileDropdownOptions;
                                    track cardOption.value
                                ) {
                                    <mat-option
                                        [value]="cardOption.value"
                                        [disabled]="cardOption.disabled"
                                    >
                                        {{ cardOption.label }}
                                    </mat-option>
                                }
                            </mat-select>
                            @if (getCardSelectionError(); as error) {
                                <mat-error>{{ error }}</mat-error>
                            }
                        </mat-form-field>

                        @if (selectedCardOnFile?.cardType) {
                            <div
                                class="flex flex-none items-center mt-[9px] ml-4"
                            >
                                <app-card-type-icon
                                    [cardType]="selectedCardOnFile.cardType"
                                />
                            </div>
                        }
                    </div>
                }
            </div>
        </mat-expansion-panel>

        <div class="flex flex-col gap-2 px-4">
            <mat-checkbox
                color="primary"
                [formControl]="informationIsVerifiedControl"
            >
                <span class="flex flex-row gap-1">
                    <span class="text-base text-tertiary-900 font-medium">
                        I have verified the above information
                    </span>
                    @if (
                        informationIsVerifiedControl.touched &&
                        informationIsVerifiedControl.invalid
                    ) {
                        <img src="assets/svg/error_icon.svg" alt="Error Icon" />
                    }
                </span>
            </mat-checkbox>

            <div class="px-3">
                <p class="text-sm text-tertiary-900">
                    After clicking "Confirm Order," you will receive an email
                    order confirmation with the information above. EWA will
                    contact you for additional information.
                </p>

                <p class="text-sm text-tertiary-900">
                    Payment will not be processed until your application is
                    reviewed by EWA, finalized, and submitted to the FCC.
                </p>
            </div>
        </div>
    }
</form>
